import React from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { PeriodDropdown, PeriodOption } from './PeriodDropdown';
import { DateRangePicker } from './DateRangePicker';
import type { CustomDateRange } from '../types';

interface BottomStatCardProps {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  bars: number[];
  theme: ThemeColors;
  period?: PeriodOption;
  onPeriodChange?: (value: PeriodOption) => void;
  periodBadge?: string; // For static badges like "N/A"
  barColor?: string;
  disabled?: boolean;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange | undefined) => void;
}

export const BottomStatCard: React.FC<BottomStatCardProps> = ({
  label,
  value,
  change,
  positive,
  bars,
  theme,
  period,
  onPeriodChange,
  periodBadge,
  barColor = '#2563eb',
  disabled = false,
  customRange,
  onCustomRangeChange,
}) => {
  const maxBar = Math.max(...bars, 1);

  // Check if the change is 0% (e.g., "+0.00%", "0%", "+0%")
  const isNoChange = !disabled && /^[+-]?0(\.0+)?%/.test(change);

  const styles = {
    bottomCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: '14px',
      position: 'relative' as const,
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    },
    statLabel: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginBottom: '6px',
      fontWeight: 500,
    },
    bottomStatValue: {
      fontSize: '22px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '4px',
    },
    statChange: {
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: 500,
      color: disabled ? theme.textSecondary : isNoChange ? '#6b7280' : positive ? '#22c55e' : '#ef4444',
    },
    barChart: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '6px',
      height: '70px',
      marginTop: '12px',
    },
    bar: {
      flex: 1,
      background: barColor,
      borderRadius: '2px',
      transition: 'height 0.3s ease',
    },
    staticBadge: {
      position: 'absolute' as const,
      top: '10px',
      right: '10px',
      background: '#6b7280',
      color: '#fff',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600,
    },
  };

  // Extract the period label from the change string (e.g., "from last week")
  const periodLabel = change.replace(/^[+-]?\d+(\.\d+)?%\s*/, '');

  return (
    <div style={styles.bottomCard}>
      {period && onPeriodChange ? (
        <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
          <PeriodDropdown
            value={period}
            onChange={onPeriodChange}
            theme={theme}
            style={{ position: 'relative', top: 'auto', right: 'auto' }}
          />
          {onCustomRangeChange && (
            <DateRangePicker
              value={customRange}
              onChange={onCustomRangeChange}
              theme={theme}
              style={{ position: 'relative', top: 'auto', right: 'auto' }}
              isActive={period === 'custom'}
            />
          )}
        </div>
      ) : periodBadge ? (
        <div style={styles.staticBadge}>{periodBadge}</div>
      ) : null}

      <div style={styles.statLabel}>{label}</div>
      <div style={styles.bottomStatValue}>{value}</div>
      <div style={styles.statChange}>
        {disabled ? (
          change
        ) : isNoChange ? (
          <>No Change {periodLabel}</>
        ) : (
          <>
            <span>{positive ? '↑' : '↓'}</span>
            {change}
          </>
        )}
      </div>
      <div style={styles.barChart}>
        {bars.map((barHeight, i) => (
          <div
            key={i}
            style={{
              ...styles.bar,
              height: `${(barHeight / maxBar) * 100}%`,
              background: barColor,
            }}
          />
        ))}
      </div>
    </div>
  );
};
